import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { imgSrcSet, imgUrl } from "@/lib/api";
import { track } from "@/lib/analytics";

export const RedactContext = createContext(false);
export const useRedacted = () => useContext(RedactContext);

export const Img = ({ src, alt, className, sizes = "(max-width: 768px) 100vw, 70vw", eager, style }) => {
  const redacted = useRedacted();
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (ref.current?.complete) setLoaded(true);
  }, [src]);
  return (
    <img
      ref={ref}
      src={imgUrl(src)}
      srcSet={imgSrcSet(src)}
      sizes={sizes}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onLoad={() => setLoaded(true)}
      style={style}
      className={cn("transition-opacity duration-700", loaded ? "opacity-100" : "opacity-0", redacted && "redacted-img", className)}
    />
  );
};

export const RedactOverlay = ({ compact }) => (
  <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center bg-black/30" data-testid="redacted-overlay">
    <div className="flex items-center gap-2 rounded-full border border-white/25 bg-black/70 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-white backdrop-blur-md">
      <Lock className="h-3 w-3" aria-hidden />
      {compact ? "Confidential" : "Confidential — imagery obscured under NDA"}
    </div>
  </div>
);

export const RedactedText = ({ text }) =>
  (text || "").split(/\[\[(.+?)\]\]/).map((part, i) =>
    i % 2 ? (
      <span key={i} className="redact-bar" aria-label="redacted">
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    )
  );

export const SectionLabel = ({ number, children, className }) => (
  <p className={cn("label-mono flex items-center gap-3", className)}>
    <span className="text-gold">{number}</span>
    <span className="h-px w-10 bg-gold/60" aria-hidden />
    <span>{children}</span>
  </p>
);

export const Reveal = ({ children, delay = 0, className, y = 28 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const STATUS_META = {
  CLIENT_WORK: { label: "Client Work", cls: "border-turq/60 bg-turq/10 text-turq" },
  CONCEPT_WORK: { label: "Concept Work", cls: "border-gold/60 bg-gold/10 text-gold" },
  PROTOTYPE: { label: "Prototype", cls: "border-silver/60 bg-silver/10 text-fg" },
  EXPERIMENTAL: { label: "Experimental", cls: "border-dashed border-turq/70 bg-canvas/40 text-fg" },
  CONFIDENTIAL: { label: "Confidential", cls: "border-fg bg-fg text-canvas" },
};

export const StatusBadge = ({ status, className }) => {
  const m = STATUS_META[status] || STATUS_META.CONCEPT_WORK;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] backdrop-blur-md", m.cls, className)}
    >
      {status === "CONFIDENTIAL" && <Lock className="h-2.5 w-2.5" aria-hidden />}
      {m.label}
    </span>
  );
};

const VARIANTS = {
  primary: "bg-turq text-onturq hover:bg-fg hover:text-canvas",
  secondary: "border border-fg/25 text-fg hover:border-turq hover:text-turq",
  ghost: "text-fg hover:text-turq px-1",
  gold: "border border-gold/60 text-gold hover:bg-gold hover:text-canvas",
};

export const ButtonLink = ({ to, variant = "primary", children, testId, className, onClick, arrow = true }) => {
  const trackClick = () => track("cta_click", { label: typeof children === "string" ? children : testId, destination: to || "action" });
  const cls = cn(
    "group inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium tracking-wide transition-[background-color,color,border-color,transform] duration-300 active:scale-[0.98]",
    VARIANTS[variant],
    className
  );
  const inner = (
    <>
      <span className={variant === "ghost" ? "link-underline pb-0.5" : ""}>{children}</span>
      {arrow && <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden />}
    </>
  );
  if (onClick)
    return (
      <button type="button" onClick={(e) => { trackClick(); onClick(e); }} className={cls} data-testid={testId}>
        {inner}
      </button>
    );
  if (to?.startsWith("http") || to?.startsWith("mailto"))
    return (
      <a href={to} onClick={trackClick} className={cls} data-testid={testId} target={to.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
        {inner}
      </a>
    );
  return (
    <Link to={to} onClick={trackClick} className={cls} data-testid={testId}>
      {inner}
    </Link>
  );
};

export const PageLoader = () => (
  <div className="grid min-h-[70vh] place-items-center" data-testid="page-loader">
    <div className="label-mono animate-pulse">Loading</div>
  </div>
);
